import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import withCustomStyles from "./ProductListPage.style";
import Grid from "@material-ui/core/Grid";
import LoadMorePart from "../parts/LoadMorePart";
import { withRouter } from "react-router";
import BreadcrumbsPart from "../parts/BreadcrumbsPart";
import { handleScroll } from "../utils/AppUtils";
import CategoryChipsPart from "../parts/CategoryChipsPart";
import ProductsListPart from "../parts/ProductsListPart";
import { productListDesc, productListTitle } from "../utils/SEOUtils";
import SEOInfoPart from "../parts/SEOInfoPart";
import {
  getCategoryPageUrl,
  getProductsPageAbsUrl,
  getProductsPageUrl
} from "../utils/UrlUtils";
import {
  getCategoryNamesTreeReverse,
  getListingPageName
} from "../utils/CategoryUtils";
import { Helmet } from "react-helmet";
import NotFoundPart from "../parts/NotFoundPart";
import ProductLoadingPart from "./ProductLoadingPart";
import CategoryChipPart from "../parts/CategoryChipPart";
import { withSnackbar } from "notistack";
import { _has } from "../utils/HelperUtils";
import { Link } from "react-router-dom";
import SearchOffIcon from "@material-ui/icons/SearchOutlined";
import { GButton, GChipRow, GEmpty } from "../parts/ui";
import SubcategoryBannerPart from "../parts/SubcategoryBannerPart";

@inject(["productsStore"])
@inject(["categoriesStore"])
@observer
class ProductListPage extends Component {
  state = {
    categoryBrandSEO: {}
  };

  componentDidMount() {
    const { productsStore, match, categoriesStore } = this.props;
    this.fetchAll();
    this.scrollListener = handleScroll.bind(this, productsStore);
    if (_has(match, "params") && match.params.brand) {
      this.setState({
        categoryBrandSEO: categoriesStore.getCategoryBrandSEO(
          match.params.brand
        )
      });
    }
    window.addEventListener("scroll", this.scrollListener);
  }

  fetchAll() {
    const {
      productsStore,
      categoriesStore,
      enqueueSnackbar,
      match: {
        params: { category, brand }
      }
    } = this.props;
    let cid = categoriesStore.getCidBySlug(category);
    if (!categoriesStore.children(cid).length) {
      categoriesStore.fetchCategoryBrands(cid, error => {
        enqueueSnackbar(error.message, {
          variant: "error"
        });
      });
    }

    this.fetchBanners(cid);

    if (brand) {
      productsStore.fetchCatBrandsProducts(brand, cid);
    } else {
      productsStore.fetch(cid);
    }
  }

  fetchBanners(cid) {
    const { categoriesStore } = this.props;
    const parentId = categoriesStore.find(cid).parent_id;
    if (parentId) {
      categoriesStore.fetchSubcategoryBanners(parentId);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      productsStore,
      categoriesStore,
      match: {
        params: { category, brand }
      }
    } = this.props;
    if (
      (category !== prevProps.match.params.category ||
        prevProps.match.params.brand) &&
      !brand
    ) {
      productsStore.cancel();
      this.fetchAll();
    } else if (brand && brand !== prevProps.match.params.brand) {
      let cid = categoriesStore.getCidBySlug(category);
      productsStore.cancel();
      productsStore.fetchCatBrandsProducts(brand, cid);
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.scrollListener);
    this.cancelAll();
  }

  cancelAll() {
    const { productsStore, categoriesStore } = this.props;
    productsStore.cancel();
    categoriesStore.cancel();
  }

  render() {
    const {
      state: { categoryBrandSEO },
      props: {
        classes,
        productsStore,
        categoriesStore,
        match: { params },
        location: { pathname }
      }
    } = this;
    let cid = categoriesStore.getCidBySlug(params.category);

    const currentCategory = categoriesStore.find(cid);
    const seoDescription = productListDesc(currentCategory.name);
    const seoTitle = productListTitle(getCategoryNamesTreeReverse(cid));

    const subcategories = categoriesStore.children(cid);
    const brands =
      subcategories.length === 0 &&
      categoriesStore.categoriesBrandsState !== "fetching"
        ? categoriesStore.categoriesBrands
        : [];
    const seoContent =
      (categoryBrandSEO && categoryBrandSEO.seo_content) ||
      (currentCategory && currentCategory.seo_content);
    const isEmpty =
      productsStore.all.length === 0 && productsStore.state === "done";

    if (productsStore.isFailed) {
      return <NotFoundPart />;
    }

    return (
      <main className={classes.page}>
        <Helmet>
          <link rel="canonical" href={getProductsPageAbsUrl(currentCategory)} />
        </Helmet>
        <SEOInfoPart
          title={
            categoryBrandSEO.seo_title || currentCategory.seo_title || seoTitle
          }
          description={
            categoryBrandSEO.seo_description ||
            currentCategory.seo_description ||
            seoDescription
          }
          image={currentCategory.image}
          imageAlt={currentCategory.name}
          url={getProductsPageAbsUrl(currentCategory)}
        />
        <BreadcrumbsPart cid={cid} />
        {(subcategories.length > 0 || brands.length > 0) && (
          <GChipRow
            label={
              subcategories.length > 0
                ? `${currentCategory.name || "Category"} sections`
                : "Filter by brand"
            }
          >
            <CategoryChipPart
              label="All"
              to={getProductsPageUrl(currentCategory)}
              isSelected={pathname === getProductsPageUrl(currentCategory)}
            />
            {subcategories.length > 0 ? (
              <CategoryChipsPart categories={subcategories} />
            ) : (
              brands.map(brand => (
                <CategoryChipPart
                  label={`${brand.name}`}
                  to={getCategoryPageUrl(brand, currentCategory)}
                  key={brand.id}
                  isSelected={brand.slug === params.brand}
                />
              ))
            )}
          </GChipRow>
        )}
        <SubcategoryBannerPart
          banner={categoriesStore.subcategoryBanners[cid]}
        />
        <div className={classes.productsWrap}>
          {isEmpty ? (
            <GEmpty
              icon={<SearchOffIcon />}
              title="Nothing here yet"
              action={
                <GButton variant="secondary" component={Link} to="/categories">
                  Browse all categories
                </GButton>
              }
            >
              Can&apos;t find what you need? Add it to your order comments and
              we&apos;ll get it for you.
            </GEmpty>
          ) : (
            <Grid container className={classes.grid}>
              {productsStore.all.length === 0 &&
                productsStore.state === "fetching" && <ProductLoadingPart />}
              <ProductsListPart
                listName={getListingPageName(currentCategory.id)}
                CTListName={"Category Listing Page"}
                products={productsStore.all}
                state={productsStore.state}
              />
            </Grid>
          )}
        </div>
        {productsStore.all.length > 1 && productsStore.state === "fetching" && (
          <LoadMorePart />
        )}
        {seoContent && (
          <section
            className={classes.seoContent}
            dangerouslySetInnerHTML={{ __html: seoContent }}
          />
        )}
      </main>
    );
  }
}

export default withRouter(withSnackbar(withCustomStyles(ProductListPage)));
